/**
 * Seating charts: seats, neighbors, constraints, and an arrangement that honors them.
 *
 * Seats are stored from the students' point of view — row 0 is the front row, column 0
 * is on the students' left as they face the board. The teacher's view is the same chart
 * turned 180°, which the widget does at render time.
 *
 * An arrangement starts from an order (random, or alphabetical by first or last name)
 * filled into the seats front to back, then repairs constraint violations one swap at a
 * time: each step takes the swap that removes the most violations. Alphabetical charts
 * prefer the shortest move, so the order survives; random charts break ties at random.
 */

import {
  randomIndex,
  seededRandom,
  shuffle,
} from "@/lib/tool-kit/classroom/random";

export type SeatingLayout =
  | { kind: "rows"; rows: number; columns: number }
  | {
      kind: "tables";
      tables: number;
      seatsPerTable: number;
      tablesPerRow: number;
    };

export interface Seat {
  index: number;
  /** 0 is the front row (of desks, or of tables). */
  row: number;
  /** Desk position from the students' left, or a table's position in its row. */
  column: number;
  /** The table a seat belongs to, or null in a rows layout. */
  table: number | null;
  front: boolean;
}

export type SeatingOrder = "random" | "first-name" | "last-name";

/** Seat index → student index (into the names list), or null for an empty seat. */
export type Assignment = (number | null)[];

export interface ResolvedConstraints {
  /** Pairs of student indices that must not sit together. */
  apart: [number, number][];
  /** Students who must sit in the front row. */
  front: number[];
  /** Names in the constraints that are not on the class list. */
  unknown: string[];
}

export type SeatingViolation =
  | { kind: "apart"; students: [number, number] }
  | { kind: "front"; student: number };

export function buildSeats(layout: SeatingLayout): Seat[] {
  const seats: Seat[] = [];
  if (layout.kind === "rows") {
    for (let row = 0; row < layout.rows; row += 1) {
      for (let column = 0; column < layout.columns; column += 1) {
        seats.push({
          index: seats.length,
          row,
          column,
          table: null,
          front: row === 0,
        });
      }
    }
    return seats;
  }
  const perRow = Math.max(1, layout.tablesPerRow);
  for (let table = 0; table < layout.tables; table += 1) {
    const row = Math.floor(table / perRow);
    for (let place = 0; place < layout.seatsPerTable; place += 1) {
      seats.push({
        index: seats.length,
        row,
        column: table % perRow,
        table,
        front: row === 0,
      });
    }
  }
  return seats;
}

/**
 * Whether two seats count as "together": the same table, or adjacent desks — side by
 * side or one behind the other, and diagonally too when `diagonal` is set.
 */
export function areNeighbors(a: Seat, b: Seat, diagonal: boolean): boolean {
  if (a.index === b.index) {
    return false;
  }
  if (a.table !== null || b.table !== null) {
    return a.table === b.table;
  }
  const rows = Math.abs(a.row - b.row);
  const columns = Math.abs(a.column - b.column);
  return diagonal ? rows <= 1 && columns <= 1 : rows + columns === 1;
}

/**
 * Seats grouped into rows as they are drawn, top row first. The student's view puts
 * the front row at the top with column 0 on the left; the teacher's view (`rotated`)
 * is the same room turned 180° — front row at the bottom and left and right swapped,
 * which is what a teacher facing the class actually sees.
 */
export function seatRows(seats: readonly Seat[], rotated: boolean): Seat[][] {
  const rows = new Map<number, Seat[]>();
  for (const seat of seats) {
    rows.set(seat.row, [...(rows.get(seat.row) ?? []), seat]);
  }
  const ordered = [...rows.keys()]
    .sort((a, b) => a - b)
    .map((row) => rows.get(row) ?? []);
  return rotated
    ? ordered.reverse().map((row) => row.slice().reverse())
    : ordered;
}

/* ---------------------------------------------------------------- constraints */

const LINE_BREAK = /\r?\n/;
const NAME_SEPARATOR = /\s*(?:,|;|\+|&|\/)\s*/;
const SPACES = /\s+/g;

function nameKey(name: string, locale?: string): string {
  return name.trim().replace(SPACES, " ").toLocaleLowerCase(locale);
}

function indexByName(
  names: readonly string[],
  locale?: string
): Map<string, number[]> {
  const byName = new Map<string, number[]>();
  for (const [index, name] of names.entries()) {
    const id = nameKey(name, locale);
    byName.set(id, [...(byName.get(id) ?? []), index]);
  }
  return byName;
}

/** Names that appear more than once — the chart cannot tell those students apart. */
export function duplicateNames(
  names: readonly string[],
  locale?: string
): string[] {
  return [...indexByName(names, locale).values()]
    .filter((indices) => indices.length > 1)
    .map((indices) => names[indices[0] as number] as string);
}

/**
 * Read the two constraint boxes.
 *
 * Keep-apart: one group per line, names separated by commas (or `+`, `&`, `/`, `;`);
 * everyone on a line is kept apart from everyone else on it. Front row: names separated
 * by commas or new lines. Matching ignores case and extra spaces.
 */
export function resolveConstraints(
  names: readonly string[],
  apartText: string,
  frontText: string,
  locale?: string
): ResolvedConstraints {
  const byName = indexByName(names, locale);
  const unknown = new Set<string>();
  const lookup = (name: string): number[] => {
    const found = byName.get(nameKey(name, locale));
    if (!found) {
      unknown.add(name.trim());
      return [];
    }
    return found;
  };

  const apart: [number, number][] = [];
  const seenPairs = new Set<string>();
  for (const line of apartText.split(LINE_BREAK)) {
    const group = line
      .split(NAME_SEPARATOR)
      .filter((name) => name.trim().length > 0)
      .flatMap(lookup);
    for (const [i, a] of group.entries()) {
      for (const b of group.slice(i + 1)) {
        const pair: [number, number] = a < b ? [a, b] : [b, a];
        const id = pair.join(",");
        if (a !== b && !seenPairs.has(id)) {
          seenPairs.add(id);
          apart.push(pair);
        }
      }
    }
  }

  const front = [
    ...new Set(
      frontText
        .split(LINE_BREAK)
        .flatMap((line) => line.split(NAME_SEPARATOR))
        .filter((name) => name.trim().length > 0)
        .flatMap(lookup)
    ),
  ];
  return { apart, front, unknown: [...unknown] };
}

/* --------------------------------------------------------------- arrangement */

function seatOfStudents(assignment: Assignment, students: number): number[] {
  const seatOf = Array.from({ length: students }, () => -1);
  for (const [seat, student] of assignment.entries()) {
    if (student !== null) {
      seatOf[student] = seat;
    }
  }
  return seatOf;
}

export function findViolations(
  assignment: Assignment,
  seats: readonly Seat[],
  constraints: ResolvedConstraints,
  diagonal: boolean,
  students: number
): SeatingViolation[] {
  const seatOf = seatOfStudents(assignment, students);
  const violations: SeatingViolation[] = [];
  for (const [a, b] of constraints.apart) {
    const seatA = seats[seatOf[a] ?? -1];
    const seatB = seats[seatOf[b] ?? -1];
    if (seatA && seatB && areNeighbors(seatA, seatB, diagonal)) {
      violations.push({ kind: "apart", students: [a, b] });
    }
  }
  for (const student of constraints.front) {
    const seat = seats[seatOf[student] ?? -1];
    if (seat && !seat.front) {
      violations.push({ kind: "front", student });
    }
  }
  return violations;
}

/** The two seats' occupants exchanged; an empty seat just receives the student. */
export function swapSeats(
  assignment: Assignment,
  a: number,
  b: number
): Assignment {
  const next = assignment.slice();
  const held = next[a] ?? null;
  next[a] = next[b] ?? null;
  next[b] = held;
  return next;
}

function startingOrder(
  names: readonly string[],
  order: SeatingOrder,
  random: () => number,
  locale?: string
): number[] {
  const indices = names.map((_, index) => index);
  if (order === "random") {
    return shuffle(indices, random);
  }
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const lastName = (name: string) => name.trim().split(SPACES).at(-1) ?? name;
  return indices.sort((a, b) => {
    const nameA = names[a] as string;
    const nameB = names[b] as string;
    const byLast =
      order === "last-name"
        ? collator.compare(lastName(nameA), lastName(nameB))
        : 0;
    return byLast || collator.compare(nameA, nameB) || a - b;
  });
}

interface RepairContext {
  seats: readonly Seat[];
  constraints: ResolvedConstraints;
  diagonal: boolean;
  students: number;
  /** Alphabetical charts break ties by the shortest move, keeping the order intact. */
  nearest: boolean;
  random: () => number;
}

function offendersOf(violations: readonly SeatingViolation[]): number[] {
  return [
    ...new Set(
      violations.flatMap((v) => (v.kind === "apart" ? v.students : [v.student]))
    ),
  ];
}

/** The single swap that removes the most violations, or null if none helps. */
function bestSwap(
  assignment: Assignment,
  violations: readonly SeatingViolation[],
  context: RepairContext
): [number, number] | null {
  const seatOf = seatOfStudents(assignment, context.students);
  let best: { swap: [number, number]; cost: number; tie: number } | null = null;
  for (const student of offendersOf(violations)) {
    const from = seatOf[student] as number;
    for (let to = 0; to < context.seats.length; to += 1) {
      if (to === from) {
        continue;
      }
      const cost = findViolations(
        swapSeats(assignment, from, to),
        context.seats,
        context.constraints,
        context.diagonal,
        context.students
      ).length;
      const tie = context.nearest ? Math.abs(from - to) : context.random();
      if (
        cost < violations.length &&
        (best === null ||
          cost < best.cost ||
          (cost === best.cost && tie < best.tie))
      ) {
        best = { swap: [from, to], cost, tie };
      }
    }
  }
  return best ? best.swap : null;
}

function kick(
  assignment: Assignment,
  violations: readonly SeatingViolation[],
  context: RepairContext
): Assignment {
  const offenders = offendersOf(violations);
  const student = offenders[randomIndex(offenders.length, context.random)];
  const from = assignment.indexOf(student ?? -1);
  const to = randomIndex(context.seats.length, context.random);
  return from >= 0 ? swapSeats(assignment, from, to) : assignment;
}

/**
 * Remove violations one best swap at a time. When no single swap helps, move one
 * offender at random and carry on; the best arrangement seen is what comes back.
 */
function repair(assignment: Assignment, context: RepairContext): Assignment {
  const count = (candidate: Assignment) =>
    findViolations(
      candidate,
      context.seats,
      context.constraints,
      context.diagonal,
      context.students
    );
  let current = assignment;
  let best = assignment;
  let bestCount = Number.POSITIVE_INFINITY;
  let kicks = 0;
  for (let step = 0; step < 400 && kicks <= 60; step += 1) {
    const violations = count(current);
    if (violations.length < bestCount) {
      best = current;
      bestCount = violations.length;
    }
    if (violations.length === 0) {
      return current;
    }
    const swap = bestSwap(current, violations, context);
    if (swap) {
      current = swapSeats(current, swap[0], swap[1]);
    } else {
      kicks += 1;
      current = kick(current, violations, context);
    }
  }
  return best;
}

/**
 * Front-row students take front seats first (in a random order of front seats for a
 * random chart, left to right otherwise); everyone else fills the remaining seats
 * front to back in the starting order.
 */
function initialAssignment(
  order: readonly number[],
  seats: readonly Seat[],
  front: readonly number[],
  /** Shuffles the front seats for a random chart; null keeps them left to right. */
  random: (() => number) | null
): Assignment {
  const assignment: Assignment = seats.map(() => null);
  const wantsFront = new Set(front);
  const frontSeats = seats
    .filter((seat) => seat.front)
    .map((seat) => seat.index);
  const slots = random === null ? frontSeats : shuffle(frontSeats, random);
  const queue: number[] = [];
  for (const student of order.filter((s) => wantsFront.has(s))) {
    const slot = slots.shift();
    if (slot === undefined) {
      queue.push(student);
    } else {
      assignment[slot] = student;
    }
  }
  queue.push(...order.filter((s) => !wantsFront.has(s)));
  for (const [seat, occupant] of assignment.entries()) {
    if (occupant === null && queue.length > 0) {
      assignment[seat] = queue.shift() ?? null;
    }
  }
  return assignment;
}

export interface ArrangeOptions {
  order: SeatingOrder;
  diagonal: boolean;
  random: () => number;
  locale?: string;
}

/**
 * Seat every student. Callers check `names.length <= seats.length` first; students
 * beyond the last seat are left out.
 */
export function arrangeStudents(
  names: readonly string[],
  seats: readonly Seat[],
  constraints: ResolvedConstraints,
  { order, diagonal, random, locale }: ArrangeOptions
): Assignment {
  const isRandom = order === "random";
  const initial = initialAssignment(
    startingOrder(names, order, random, locale),
    seats,
    constraints.front,
    isRandom ? random : null
  );
  return repair(initial, {
    seats,
    constraints,
    diagonal,
    students: names.length,
    nearest: !isRandom,
    // An alphabetical chart must come out the same every time for the same class.
    random: isRandom ? random : seededRandom(names.length + seats.length),
  });
}
